import { HolidaySortField, PaginatedHolidaysQueryVariables } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SortDropdownFilter from "../../Shared/SortDropdownFilter";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: PaginatedHolidaysQueryVariables) => void;
}

/**
 * HolidaysSortingDropdown component
 * Provides a dropdown menu for sorting holiday data by different fields and orders
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting the updated query variables
 * @returns {JSX.Element} Sorting dropdown component
 */
const HolidaysSortingDropdown = (props: Props) => {
  const { t } = useTranslation(["translation", "common", "components"]);

  /**
   * Gets user-friendly names for sort fields
   * @param {HolidaySortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: HolidaySortField): string => {
    switch (field) {
    case HolidaySortField.Province:
      return t("components:filters.holidays.sortfields.province");
    case HolidaySortField.Date:
      return t("components:filters.holidays.sortfields.date");
    default:
      return field;
    }
  };

  const holidaySortConfig = {
    paginationStateKey: "paginatedHolidays" as keyof PaginationState,
    sortFields: HolidaySortField,
    getSortFieldName,
    defaultSortField: HolidaySortField.Date,
    buildQueryVariables: (displayAmount: number, filters: any, sort: { field: HolidaySortField; order: any } | null): PaginatedHolidaysQueryVariables => ({
      first: displayAmount,
      filters: {
        date: filters.date,
        provinces: filters.provinces,
      },
      sort: sort
    }),
    extractFilters: (paginationState: any) => ({
      date: paginationState.filters?.date,
      provinces: paginationState.filters?.provinces,
    })
  };

  return (
    <SortDropdownFilter
      displayAmount={props.displayAmount}
      handleSubmitClick={props.handleSubmitClick}
      config={holidaySortConfig}
    />
  );
};

export default HolidaysSortingDropdown;
